import { useParams, usePathname, useSearchParams } from 'next/navigation'
import { useEffect, useMemo, useRef, useState } from 'react'

import { useCatalogInitialData } from '@/features/catalog/catalog-initial-data'
import { getCatalogUrlParts, useCatalogUrl } from '@/features/catalog/lib/catalog-url-context'

import { catalogApiSlice, CatalogProductsData, createCatalogUrlParams } from '@/entities/catalog-old'
import { getRegionCookies } from '@/entities/regions'

import { TProduct } from '@/shared/types/product-types'

const getCategoryCodeFromSlug = (slug: string | string[] | undefined) => (Array.isArray(slug) ? slug[0] : slug)

const getSafePage = (pageValue: string | null): number => {
	const parsedPage = pageValue ? Number(pageValue) : 1

	if (!Number.isFinite(parsedPage) || parsedPage < 1) return 1
	return Math.floor(parsedPage)
}

export const useCatalogProducts = (filterUrl: string) => {
	const pathname = usePathname()
	const searchParams = useSearchParams()
	const catalogUrlState = useCatalogUrl()
	const catalogUrl =
		catalogUrlState?.resolvedUrl ?? `${pathname}${searchParams.toString() ? `?${searchParams.toString()}` : ''}`
	const { pathname: resolvedPathname, search } = getCatalogUrlParts(catalogUrl)
	const params = useParams()
	const slugParam = params?.slug as string | string[] | undefined
	const categoryCode = getCategoryCodeFromSlug(slugParam)

	const effectiveSearchParams = new URLSearchParams(search)
	const queryPage = effectiveSearchParams.get('page')
	const querySort = effectiveSearchParams.get('sort')
	const sort = querySort ? querySort.toString() : `popular`
	const page = getSafePage(queryPage)
	const regionCookies = getRegionCookies()

	const { initialProductsData, initialResolvedUrl } = useCatalogInitialData()
	const canUseInitialForCurrentRoute = initialResolvedUrl === catalogUrl

	const [fetchMoreData] = catalogApiSlice.useLazyGetMainCatalogProductsQuery()
	const [moreData, setMoreData] = useState<Omit<CatalogProductsData, 'ITEMS'> | null>(null)
	const [moreProductItems, setMordeProductItems] = useState<TProduct[]>([])

	const queryParams = useMemo(() => createCatalogUrlParams({ sort, page }), [sort, page])
	const queryParamsKey = useMemo(() => JSON.stringify(queryParams), [queryParams])
	// Anchor the "initial request" args to the SSR data SEED, not to the live args. The seed key only
	// changes when a new initialProductsData arrives (route/category change), so the anchor stays put
	// while the user filters. Keying it on filterUrl/queryParamsKey (as before) re-captured the anchor
	// on every interaction, so isInitialRequestArgs was always true and the downgrade guard below never
	// released — suppressing the optimistic filtered products on the first click until the full
	// sef-url → resolvedUrl → canonical cycle landed.
	const initialSeedKey = `${categoryCode || ''}-${initialResolvedUrl || ''}-${initialProductsData?.TOTAL || 0}-${
		initialProductsData?.ITEMS?.length || 0
	}`
	const initialSeedRef = useRef('')
	const initialRequestRef = useRef<{ queryParamsKey: string; filterUrl: string } | null>(null)

	if (initialSeedRef.current !== initialSeedKey) {
		initialSeedRef.current = initialSeedKey
		initialRequestRef.current = {
			queryParamsKey,
			filterUrl
		}
	}

	// True only while the live args still match the ones that produced the SSR seed. Flips to false the
	// moment the user changes the filter (or sort/page), releasing the downgrade protection so a smaller
	// filtered result can render immediately.
	const isInitialRequestArgs = Boolean(
		initialRequestRef.current &&
			initialRequestRef.current.queryParamsKey === queryParamsKey &&
			initialRequestRef.current.filterUrl === filterUrl
	)
	const isFilterRoute = resolvedPathname.includes('/filter/')

	const productsQueryArgs = useMemo(
		() => ({ params: queryParams, filterUrl, regionCookies }),
		[queryParams, filterUrl, regionCookies]
	)

	const {
		data: fetchedProductData,
		isFetching: isLazyProductFetching,
		isLoading: isLazyProductLoading
	} = catalogApiSlice.useGetMainCatalogProductsQuery(productsQueryArgs, {
		skip: !categoryCode || !filterUrl,
		// Don't re-fetch the product list on tab refocus (global apiSlice enables it).
		refetchOnFocus: false
	})

	const hasQueryItems = Boolean(fetchedProductData?.ITEMS?.length)
	const initialTotal = initialProductsData?.TOTAL || 0
	const queryTotal = fetchedProductData?.TOTAL || 0
	const shouldProtectInitialFromDowngrade = Boolean(
		initialProductsData?.ITEMS?.length && canUseInitialForCurrentRoute && isInitialRequestArgs
	)
	const shouldUseQueryData = isFilterRoute
		? Boolean(fetchedProductData)
		: hasQueryItems && (!shouldProtectInitialFromDowngrade || queryTotal >= initialTotal)
	const effectiveProductData = shouldUseQueryData ? fetchedProductData : initialProductsData

	useEffect(() => {
		setMoreData(null)
		setMordeProductItems([])
	}, [page, sort, filterUrl])

	const getMoreProducts = async (page: number) => {
		const urlParams = createCatalogUrlParams({ sort, page })

		const moreDataRes = await fetchMoreData({ params: urlParams, filterUrl, regionCookies }, true)

		if (moreDataRes?.data) {
			const { ITEMS, ...moreData } = moreDataRes?.data
			setMoreData(moreData)
			setMordeProductItems(prevState => [...prevState, ...ITEMS])
		}
	}

	const products = useMemo(
		() => [...(effectiveProductData?.ITEMS || []), ...moreProductItems],
		[effectiveProductData, moreProductItems]
	)

	return {
		products,
		currentPage: moreData?.CURRENT_PAGE || effectiveProductData?.CURRENT_PAGE || 0,
		lastPage: effectiveProductData?.LAST_PAGE || 0,
		totalProducts: effectiveProductData?.TOTAL || 0,
		loading: (isLazyProductLoading || isLazyProductFetching) && !shouldProtectInitialFromDowngrade,
		getMoreProducts
	}
}
